import {
  Badge,
  Button,
  Checkbox,
  Flex,
  FormLabel,
  Heading,
  Input,
  InputAddon,
  InputGroup,
  Select,
  Skeleton,
  Spacer,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'

import Paginator from '../../../components/paginator'
import { capitalize } from '../../../helpers'
import { FormLeadService } from '../../../services/FormLead/FormLeadService'
import { FormLeadCreateModal } from './FormLeadCreateModal'
import { FormLeadDeleteDialog } from './FormLeadDeleteDialog'
import { FormLeadDeleteMultipleDialog } from './FormLeadDeleteMultipleDialog'
import { FormLeadsEditModal } from './FormLeadsEditModal'
import { IFormLead, IFormLeadStatus, statusColors, statusTranslations } from './models'

export default function FormLeadsPage() {
  const service = new FormLeadService()
  const cancelRef = useRef()
  const [isLoading, setIsLoading] = useState(false)
  const [leads, setLeads] = useState<IFormLead[]>([])
  const [length, setLength] = useState(25)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [draw, setDraw] = useState(0)
  const [search, setSearch] = useState('')
  const [modalLead, setModalLead] = useState<IFormLead | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [sellers, setSellers] = useState<string[]>([])
  const [leadsCounting, setLeadsCounting] = useState<{
    loading: boolean
    total: number
    Qualificado: number
    Desqualificado: number
    'Sem Contato': number
  }>({
    loading: true,
    total: 0,
    Qualificado: 0,
    Desqualificado: 0,
    'Sem Contato': 0
  })
  const { isOpen: deleteDialogIsOpen, onOpen: deleteDialogOnOpen, onClose: deleteDialogIsClose } = useDisclosure()
  const {
    isOpen: deleteMultipleDialogIsOpen,
    onOpen: deleteMultipleDialogOnOpen,
    onClose: deleteMultipleDialogIsClose
  } = useDisclosure()
  const [leadToDelete, setLeadToDelete] = useState<IFormLead>()
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const [filters, setFilters] = useState<{
    status?: IFormLeadStatus
    format: string[]
    course: string
    consultant: string
    period: {
      from: string
      to: string
    }
  }>({
    format: [],
    course: '',
    consultant: '',
    period: { from: '', to: '' }
  })
  const [updatingStatus, setUpdatingStatus] = useState<number[]>([])
  const [isEditingStatus, setIsEditingStatus] = useState<number[]>([])

  const loadSellers = async () => {
    setSellers(await service.getSellers())
  }

  const loadQuantities = async () => {
    setLeadsCounting({ ...leadsCounting, loading: true })

    const quantities = await service.getQuantities({
      consultant: filters.consultant,
      course: filters.course,
      format: filters.format,
      period: {
        from: filters.period.from,
        to: filters.period.to
      },
      search: search,
      status: filters.status
    })

    setLeadsCounting({ ...quantities, loading: false })
  }

  const order: { column: number; dir: string } = { column: 0, dir: 'desc' }

  const loadLeads = async () => {
    setIsLoading(true)
    setSelectedItems([])
    loadQuantities()
    const leadsList = await service.list({
      draw,
      length,
      start: (page - 1) * length,
      order: [order],
      search: { value: search },
      status: filters.status,
      format: filters.format,
      course: filters.course,
      consultant: filters.consultant,
      period: { from: filters.period?.from || '', to: filters.period?.to || '' }
    })

    setDraw(draw + 1)

    setTotalPages(Math.ceil(leadsList.recordsFiltered / length))

    setLeads(leadsList.data)

    setIsLoading(false)
  }

  useEffect(() => {
    loadLeads()
  }, [page, search, length, filters])

  useEffect(() => {
    loadSellers()
  }, [])

  const handleOpenModal = async (lead: IFormLead) => {
    setModalLead(lead)
    setIsModalOpen(true)
  }

  const handleDeleteDialog = (lead: IFormLead) => {
    deleteDialogOnOpen()
    setLeadToDelete(lead)
  }

  const handleStatusChange = async (newStatus: IFormLeadStatus, id: number) => {
    setUpdatingStatus([...updatingStatus, id])

    await service.updateStatus(id, newStatus)
    loadQuantities()

    const formLeads = [...leads]
    const leadIndex = formLeads.findIndex((lead) => lead.id === id)
    formLeads[leadIndex].status = newStatus

    setLeads(leads)

    setUpdatingStatus(updatingStatus.filter((statusId) => statusId !== id))
    setIsEditingStatus(isEditingStatus.filter((statusId) => statusId !== id))
  }

  return (
    <>
      <Flex mb="2" justifyContent="space-between" direction="row">
        <Heading as="h1" size="lg" mb="10" textTransform="uppercase">
          Base de Leads
        </Heading>
        <div>
          <Text
            fontSize="md"
            color="gray.500"
            textAlign="right"
            mr="2"
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            Total de leads:{' '}
            {leadsCounting.loading ? <Skeleton w={10} h={4} display={'inline-block'} ms={1} /> : leadsCounting.total}
          </Text>
          <Text
            fontSize="md"
            color="gray.500"
            textAlign="right"
            mr="2"
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            Qualificados:{' '}
            {leadsCounting.loading ? (
              <Skeleton w={10} h={4} display={'inline-block'} ms={1} />
            ) : (
              leadsCounting.Qualificado || 0
            )}
          </Text>
          <Text
            fontSize="md"
            color="gray.500"
            textAlign="right"
            mr="2"
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            Desqualificados:{' '}
            {leadsCounting.loading ? (
              <Skeleton w={10} h={4} display={'inline-block'} ms={1} />
            ) : (
              leadsCounting.Desqualificado || 0
            )}
          </Text>
          <Text
            fontSize="md"
            color="gray.500"
            textAlign="right"
            mr="2"
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            Sem contato:{' '}
            {leadsCounting.loading ? (
              <Skeleton w={10} h={4} display={'inline-block'} ms={1} />
            ) : (
              leadsCounting['Sem Contato'] || 0
            )}
          </Text>
        </div>
      </Flex>
      <div className="row mb-3">
        <div className="col-12 col-md">
          <FormLabel htmlFor="teacher" fontSize="sm" fontWeight="normal">
            Status
          </FormLabel>
          <Select
            id="status"
            size="sm"
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value as IFormLeadStatus })}
          >
            <option value="">Selecione</option>
            <option value="Sem Contato">{statusTranslations['Sem Contato']}</option>
            <option value="Qualificado">{statusTranslations.Qualificado}</option>
            <option value="Desqualificado">{statusTranslations.Desqualificado}</option>
          </Select>
        </div>
        <div className="col-12 col-md">
          <FormLabel htmlFor="teacher" fontSize="sm" fontWeight="normal">
            Formato
          </FormLabel>
          <Flex alignItems="center" h="32px" gap="2">
            <Checkbox
              size="sm"
              colorScheme="magenta"
              isChecked={filters.format.includes('Online')}
              onChange={(e) => {
                const newFormat = e.target.checked
                  ? [...filters.format, 'Online']
                  : filters.format.filter((format) => format !== 'Online')
                setFilters({ ...filters, format: newFormat })
              }}
            >
              Online
            </Checkbox>
            <Checkbox
              size="sm"
              colorScheme="magenta"
              isChecked={filters.format.includes('Online Ao Vivo')}
              onChange={(e) => {
                const newFormat = e.target.checked
                  ? [...filters.format, 'Online Ao Vivo']
                  : filters.format.filter((format) => format !== 'Online Ao Vivo')
                setFilters({ ...filters, format: newFormat })
              }}
            >
              Online Ao Vivo
            </Checkbox>
            <Checkbox
              size="sm"
              colorScheme="magenta"
              isChecked={filters.format.includes('Presencial')}
              onChange={(e) => {
                const newFormat = e.target.checked
                  ? [...filters.format, 'Presencial']
                  : filters.format.filter((format) => format !== 'Presencial')
                setFilters({ ...filters, format: newFormat })
              }}
            >
              Presencial
            </Checkbox>
          </Flex>
        </div>
        <div className="col-12 col-md">
          <FormLabel htmlFor="teacher" fontSize="sm" fontWeight="normal">
            Responsável
          </FormLabel>
          <Select
            id="consultant"
            size="sm"
            value={filters.consultant}
            onChange={(e) => setFilters({ ...filters, consultant: e.target.value })}
          >
            <option value="">Selecione</option>
            {sellers.map((seller) => (
              <option key={seller} value={seller}>
                {seller}
              </option>
            ))}
          </Select>
        </div>
        <div className="col-12 col-md-4">
          <FormLabel htmlFor="date-start" fontSize="sm" fontWeight="normal">
            Data da solicitação
          </FormLabel>
          <InputGroup display="flex" alignItems="center" size="sm">
            <Input
              type="date"
              size="sm"
              variant="outline"
              id="date-start"
              value={filters.period.from}
              onChange={(e) => setFilters({ ...filters, period: { ...filters.period, from: e.target.value } })}
              max={filters.period.to}
            />
            <InputAddon>Até</InputAddon>
            <Input
              type="date"
              size="sm"
              variant="outline"
              id="date-end"
              value={filters.period.to}
              onChange={(e) => setFilters({ ...filters, period: { ...filters.period, to: e.target.value } })}
              min={filters.period.from}
            />
          </InputGroup>
        </div>
      </div>
      <Flex mb="4">
        <Flex flex="1" alignItems="center">
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            Mostrar
          </FormLabel>
          <Select
            size="sm"
            variant="outline"
            id="per-page"
            w="75px"
            mr="2"
            onChange={(e) => setLength(parseInt(e.target.value))}
            value={length}
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </Select>
          <FormLabel htmlFor="per-page" fontSize="sm" fontWeight="normal">
            resultados por página
          </FormLabel>
        </Flex>
        <Spacer />
        <Flex flex="1" justifyContent="flex-end" alignItems="center">
          <FormLabel htmlFor="search" fontSize="sm" fontWeight="normal" p={0} m={0} mr={2}>
            Pesquisar
          </FormLabel>
          <Input
            type="search"
            size="sm"
            variant="outline"
            id="search"
            w="200px"
            onChange={(e) => setSearch(e.target.value)}
            value={search}
          />
        </Flex>
      </Flex>
      <TableContainer fontSize="smaller" padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <Th p={1} verticalAlign={'middle'} textAlign="center">
                <Checkbox
                  size="sm"
                  colorScheme="magenta"
                  isChecked={selectedItems.length === leads.length && !isLoading}
                  isIndeterminate={selectedItems.length > 0 && selectedItems.length < leads.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedItems(leads.map((lead) => lead.id))
                    } else {
                      setSelectedItems([])
                    }
                  }}
                />
              </Th>
              <Th p={1}>ID</Th>
              <Th p={1}>Nome</Th>
              <Th p={1}>E-mail</Th>
              <Th p={1}>Telefone</Th>
              <Th p={1}>Formato</Th>
              <Th p={1}>Curso</Th>
              <Th p={1}>Origem</Th>
              <Th p={1}>Responsável</Th>
              <Th p={1}>Status</Th>
              <Th p={1}>Criado em</Th>
              <Th p={1}>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {leads.map((lead) => (
              <Tr key={lead.id}>
                <Td verticalAlign={'middle'} textAlign="center">
                  <Checkbox
                    size="sm"
                    colorScheme="magenta"
                    isChecked={selectedItems.includes(lead.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedItems([...selectedItems, lead.id])
                      } else {
                        setSelectedItems(selectedItems.filter((id) => id !== lead.id))
                      }
                    }}
                  />
                </Td>
                <Td p={1}>{lead.id}</Td>
                <Td p={1}>{lead.name}</Td>
                <Td p={1}>{lead.email}</Td>
                <Td p={1}>{lead.phone}</Td>
                <Td p={1}>{capitalize(lead.format)}</Td>
                <Td p={1}>
                  {lead.course} ({lead.area})
                </Td>
                <Td p={1}>{lead.source}</Td>
                <Td p={1}>{lead.seller_name}</Td>
                <Td p={1} textAlign="center" w="180px">
                  {isEditingStatus.includes(lead.id) ? (
                    <InputGroup display="flex" alignItems="center">
                      <Select
                        size="xs"
                        fontSize={'0.8em'}
                        variant="outline"
                        value={lead.status}
                        onChange={(event) => handleStatusChange(event.target.value as IFormLeadStatus, lead.id)}
                        disabled={updatingStatus.includes(lead.id)}
                      >
                        <option value="Sem Contato">{statusTranslations['Sem Contato']}</option>
                        <option value="Qualificado">{statusTranslations.Qualificado}</option>
                        <option value="Desqualificado">{statusTranslations.Desqualificado}</option>
                      </Select>
                      <Tooltip label="Cancelar" aria-label="Cancelar">
                        <Button
                          size="xs"
                          colorScheme="red"
                          ml="1"
                          onClick={() => setIsEditingStatus(isEditingStatus.filter((statusId) => statusId !== lead.id))}
                        >
                          <i className="lni lni-close"></i>
                        </Button>
                      </Tooltip>
                    </InputGroup>
                  ) : (
                    <Tooltip label="Clique duas vezes para editar" aria-label="Clique duas vezes para editar">
                      <Badge
                        colorScheme={statusColors[lead.status]}
                        fontSize="0.8em"
                        onDoubleClick={() => setIsEditingStatus([...isEditingStatus, lead.id])}
                      >
                        {statusTranslations[lead.status]}
                      </Badge>
                    </Tooltip>
                  )}
                </Td>
                <Td p={1}>
                  <Tooltip label={new Date(lead.created_at).toLocaleString('pt-BR').replace(',', ' -')}>
                    {new Date(lead.created_at).toLocaleString('pt-BR').slice(0, -10)}
                  </Tooltip>
                </Td>
                <Td p={1}>
                  <Button
                    onClick={() => handleOpenModal(lead)}
                    colorScheme="magenta"
                    variant="outline"
                    size="xs"
                    mb="2"
                    ml="0"
                    mr="0"
                  >
                    <i className="lni lni-pencil"></i>
                  </Button>
                  <Button onClick={() => handleDeleteDialog(lead)} colorScheme="magenta" size="xs" mb="2" ml="2" mr="0">
                    <i className="lni lni-trash-can"></i>
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
      />

      <FormLeadDeleteDialog
        isOpen={deleteDialogIsOpen}
        onClose={deleteDialogIsClose}
        lead={leadToDelete}
        leastDestructiveRef={cancelRef}
        onDelete={() => {
          deleteDialogIsClose()
          loadLeads()
        }}
      />

      <FormLeadDeleteMultipleDialog
        isOpen={deleteMultipleDialogIsOpen}
        onClose={deleteMultipleDialogIsClose}
        leads={selectedItems}
        leastDestructiveRef={cancelRef}
        onDelete={() => {
          deleteMultipleDialogIsClose()
          loadLeads()
        }}
      />

      <FormLeadCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false)
          loadLeads()
        }}
        sellers={sellers}
      />

      <FormLeadsEditModal
        lead={modalLead}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          loadLeads()
        }}
        sellers={sellers}
      />

      <Tooltip label="Adicionar manualmente" aria-label="Adicionar manualmente" placement="left">
        <Button
          colorScheme="magenta"
          position="fixed"
          bottom="20px"
          right="20px"
          borderRadius="full"
          width="70px"
          height="70px"
          onClick={() => {
            setIsCreateModalOpen(true)
          }}
        >
          <i className="lni lni-plus"></i>
        </Button>
      </Tooltip>

      {selectedItems.length > 0 && (
        <Tooltip label="Excluir selecionados" aria-label="Excluir selecionados" placement="left">
          <Button
            colorScheme="magenta"
            position="fixed"
            bottom="100px"
            right="30px"
            borderRadius="full"
            width="50px"
            height="50px"
            variant="outline"
            backgroundColor="white"
            onClick={() => {
              deleteMultipleDialogOnOpen()
            }}
          >
            <i className="lni lni-trash-can"></i>
          </Button>
        </Tooltip>
      )}
    </>
  )
}
